import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import Seo from "../components/Seo";
import { META } from "../lib/seo-meta";
import { routePath, blogArticlePath } from "../lib/routes";

export default function Blog({ lang = "en" }) {
  const { t } = useI18n();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const m = META.blog[lang];

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/public/blog", { params: { language: lang } })
      .then(({ data }) => setArticles(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [lang]);

  const articleUrl = (a) => blogArticlePath(lang, a.slug);

  return (
    <div data-testid="blog-public" className="min-h-screen bg-white">
      <Seo pageKey="blog" lang={lang} title={m.title} description={m.description} image={m.image} />
      <Nav solid />
      <div className="h-[60px] md:h-[68px]" />
      <main id="main" className="mx-auto max-w-5xl px-6 py-16 md:py-24">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0e7c6b]">{t("blog.eyebrow")}</p>
        <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-[#050505] md:text-6xl">{t("blog.title")}</h1>
        <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#6b6f6c]">{t("blog.subtitle")}</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 size={22} className="animate-spin text-[#9a9d98]" /></div>
        ) : articles.length === 0 ? (
          <p className="py-24 text-center text-sm font-light text-[#9a9d98]" data-testid="blog-public-empty">{t("blog.empty")}</p>
        ) : (
          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {articles.map((a) => (
              <Link key={a.slug} to={articleUrl(a)} data-testid={`blog-public-card-${a.slug}`}
                className="group block overflow-hidden rounded-3xl border border-black/8 bg-white transition-shadow hover:shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
                {a.hero_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-[#f0efec]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${a.hero_url}`} alt={a.title} loading="lazy" width="800" height="450"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                ) : <div className="aspect-[16/9] bg-gradient-to-br from-[#0e7c6b]/10 to-[#141514]/5" />}
                <div className="p-6">
                  {a.published_at && <p className="text-[11px] font-medium uppercase tracking-wide text-[#9a9d98]">{a.published_at.slice(0, 10)}</p>}
                  <h2 className="mt-2 font-display text-xl font-normal leading-snug text-[#050505]">{a.title}</h2>
                  <p className="mt-2 line-clamp-2 text-sm font-light leading-relaxed text-[#6b6f6c]">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0e7c6b]">{t("blog.read")} <ArrowUpRight size={15} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
